import { Component, input, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { ProjectResponseDashboardDto } from '../../api/models/project-response-dashboard-dto';
import { ApiConfiguration } from '../../api/api-configuration';
import {
  dashboardProjectsControllerGetProjectContributorsV1,
  DashboardProjectsControllerGetProjectContributorsV1$Params,
} from '../../api/fn/dashboard-projects/dashboard-projects-controller-get-project-contributors-v-1';
import { ProjectStatsService } from '../../services/project-stats.service';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';
import {
  ContributorsModalComponent,
  Contributor,
} from '../contributors-modal/contributors-modal.component';

@Component({
  selector: 'app-raised-amounts-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, CardModule, CurrencyFormatPipe],
  providers: [DialogService],
  templateUrl: './raised-amounts-card.component.html',
  styleUrl: './raised-amounts-card.component.css',
})
export class RaisedAmountsCardComponent {
  private readonly dialogService = inject(DialogService);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly messageService = inject(MessageService);
  private readonly currencyPipe = new CurrencyFormatPipe();
  protected readonly statsService = inject(ProjectStatsService);

  project = input.required<ProjectResponseDashboardDto>();

  constructor() {
    // Load stats when project changes
    effect(() => {
      const project = this.project();
      if (project?.id) {
        this.statsService.loadStats(project.id);
      }
    });
  }

  protected async onViewContributorsClick(): Promise<void> {
    const project = this.project();
    if (!project?.id) return;

    try {
      const params: DashboardProjectsControllerGetProjectContributorsV1$Params = {
        projectId: project.id,
        limit: 50,
      };

      const response = await firstValueFrom(
        dashboardProjectsControllerGetProjectContributorsV1(
          this.http,
          this.apiConfig.rootUrl,
          params
        )
      );

      const contributors: Contributor[] = (response.body?.contributors ?? []).map((c) => ({
        rank: c.rank,
        walletAddress: c.walletAddress,
        contributedAmount: `${this.currencyPipe.transform(c.totalContributed)} USDT`,
      }));

      this.dialogService.open(ContributorsModalComponent, {
        header: '',
        width: '515px',
        modal: true,
        closable: false,
        closeOnEscape: true,
        styleClass: 'mosaic-dialog',
        maskStyleClass: 'transparent-mask',
        focusOnShow: false,
        data: {
          contributors,
          totalContributors: response.body?.totalContributors ?? 0,
          projectId: project.id,
        },
      });
    } catch (error) {
      console.error('Failed to load contributors:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load contributors. Please try again.',
        life: 3000,
      });
    }
  }
}
